import type { Tenant } from "@core/schema/Tenant.js";
import { getCartAddressPath } from "@core/schema/paths/getCartAddressPath.js";
import { getCartPath } from "@core/schema/paths/getCartPath.js";

export enum CartStep {
	PaymentDelivery = "PaymentDelivery",
	Address = "Address",
	ReadyToSend = "ReadyToSend",
}

export const cartStepArray = [CartStep.PaymentDelivery, CartStep.Address, CartStep.ReadyToSend] as const;

export type StepIndex<Step extends CartStep, Arr = typeof cartStepArray> = keyof {
	[K in keyof Arr as Arr[K] extends Step ? K : never]: unknown;
};

export type NextStep<Step extends CartStep> =
	StepIndex<Step, [null, ...typeof cartStepArray]> extends infer I extends Extract<
		keyof typeof cartStepArray,
		`${number}`
	>
		? (typeof cartStepArray)[I]
		: never;

type DeleteFirst<T extends readonly unknown[]> = T extends readonly [unknown, ...infer R] ? R : never;

export type PreviousStep<Step extends CartStep> =
	StepIndex<Step, DeleteFirst<typeof cartStepArray>> extends infer I extends Extract<
		keyof typeof cartStepArray,
		`${number}`
	>
		? (typeof cartStepArray)[I]
		: never;

export type NonEdgeStep = PreviousStep<
	keyof {
		[K in CartStep as CartStep extends PreviousStep<K> ? never : NextStep<K>]: true;
	}
>;

export function getNextStep<Step extends CartStep>(step: Step): NextStep<Step> {
	const nextStep = cartStepArray[cartStepArray.indexOf(step) + 1];
	if (nextStep !== undefined) {
		return nextStep as NextStep<Step>;
	}
	throw new Error(`No next step for ${step}`);
}

export function getPreviousStep<Step extends CartStep>(step: Step): PreviousStep<Step> {
	const previousStep = cartStepArray[cartStepArray.indexOf(step) - 1];
	if (previousStep !== undefined) {
		return previousStep as PreviousStep<Step>;
	}
	throw new Error(`No previous step for ${step}`);
}

export function getCartStepPath(step: CartStep, tenant: Tenant): string {
	switch (step) {
		case CartStep.PaymentDelivery:
			return getCartPath(tenant);
		case CartStep.ReadyToSend:
		case CartStep.Address:
			return getCartAddressPath(tenant);
	}
}
